/**
 * What a records page remembers between visits: which saved view was open, and what the reader had
 * changed about it that they never saved.
 *
 * **One record per reader, per page.** The reader is in the key because these pages are read on
 * shared clinic workstations, and a filter value is a client's name as often as it is a status.
 * Without it the next person to sign in on that machine opens the previous one's filters.
 *
 * **A clean view is stored as an id and nothing else.** Storing its contents as well would mean
 * restoring a copy rather than the view: a shared view edited by someone else in the meantime would
 * come back as it was, marked as the reader's own unsaved changes. So the contents are kept only
 * when they differ from the baseline, which is the view's current definition when one is open and
 * the collection's defaults when none is.
 */

const STORAGE_PREFIX = 'records-view-state';

/*
 * In the key rather than in the record. A shape change then reads as absent — the reader falls back
 * to the collection's defaults — instead of being parsed as something it is not.
 */
const STORAGE_VERSION = 'v1';

/**
 * @typedef {object} RecordsWorkingState
 * @property {string[]} columns
 * @property {object} filters
 * @property {object} otherSettings
 * @property {number|null} rowsPerPage
 */

/**
 * @typedef {object} RecordsViewState
 * @property {string|null} viewId Saved view currently open, or null for the collection itself.
 * @property {RecordsWorkingState|null} edits What the reader changed, or null when nothing is changed.
 */

/**
 * Where one reader's state for one page lives.
 *
 * @param {string} userId
 * @param {string} pageId - The page's `saveFilterId`.
 * @returns {string}
 */
export const viewStateKey = (userId, pageId) => (
  `${STORAGE_PREFIX}:${STORAGE_VERSION}:${userId}:${pageId}`
);

/*
 * Key order is not part of the state, so it is not allowed to be part of the comparison. `filters`
 * is a map that gets rebuilt rather than mutated — removing one returns a fresh object and the
 * dialog appends what is added — so taking a filter off and putting the same one back moves its
 * key to the end. Without this that reads as an unsaved edit nobody made, and the view gets stored
 * as a copy of itself.
 *
 * Arrays keep their order: the columns are a sequence, and a reordered one is a real change.
 */
const orderedKeys = (key, value) => (
  (value !== null && typeof value === 'object' && !Array.isArray(value))
    ? Object.fromEntries(Object.keys(value).sort().map((name) => [name, value[name]]))
    : value
);

/**
 * The working state as one comparable string.
 *
 * Page is left out on purpose: paging through a view is how it is read, not a change to it.
 * Searching is left out because it is never stored, so it could only ever read as an edit that
 * cannot be restored.
 *
 * @param {RecordsWorkingState|null} state
 * @returns {string}
 */
export const stateSnapshot = (state) => JSON.stringify({
  columns: state?.columns ?? [],
  filters: state?.filters ?? {},
  otherSettings: state?.otherSettings ?? {},
  rowsPerPage: state?.rowsPerPage ?? null,
}, orderedKeys);

/*
 * Storage can throw rather than simply be empty — Safari in private browsing, a device out of quota,
 * a browser set to block site data. None of it is worth failing a page load over: a reader who
 * cannot be remembered gets the collection's defaults, which is what a first visit gets anyway.
 */
const withStorage = (run, fallback = null) => {
  try {
    return run(window.localStorage);
  } catch {
    return fallback;
  }
};

/**
 * @param {string} userId
 * @param {string} pageId
 * @returns {RecordsViewState|null} Null when nothing is stored, or what is stored cannot be read.
 */
export const readViewState = (userId, pageId) => withStorage((storage) => {
  const stored = storage.getItem(viewStateKey(userId, pageId));
  if (stored === null) {
    return null;
  }
  const record = JSON.parse(stored);
  return {
    viewId: record?.viewId ?? null,
    edits: record?.edits ?? null,
  };
});

/**
 * @param {string} userId
 * @param {string} pageId
 * @param {RecordsViewState} record
 * @returns {void}
 */
export const writeViewState = (userId, pageId, record) => withStorage((storage) => {
  storage.setItem(viewStateKey(userId, pageId), JSON.stringify({
    viewId: record?.viewId ?? null,
    edits: record?.edits ?? null,
  }));
});

/**
 * @param {string} userId
 * @param {string} pageId
 * @returns {void}
 */
export const clearViewState = (userId, pageId) => withStorage((storage) => {
  storage.removeItem(viewStateKey(userId, pageId));
});

/**
 * Every reader's records state on this device, for signing out.
 *
 * Keying by reader stops one person opening another's filters; it does not stop those filters
 * sitting on the machine afterwards, which on a shared clinic workstation is most of the point. The
 * sweep lives here rather than in the sign-out path so that the shape of these keys stays the one
 * thing this module is responsible for.
 *
 * @returns {void}
 */
export const clearAllViewState = () => withStorage((storage) => {
  Object.keys(storage)
    .filter((key) => key.startsWith(`${STORAGE_PREFIX}:`))
    .forEach((key) => storage.removeItem(key));
});
